import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, Trash2, Plus } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useDroppable } from '@dnd-kit/core';
import {
    ROOM_CARD_BASE,
    ROOM_CARD_INSURER_BORDER,
    ROOM_CARD_CONTRACTOR_BORDER,
    ROOM_CARD_HIGHLIGHTED,
    ROOM_CARD_LOCKED,
    ROOM_CARD_INVALID_TARGET,
    CHEVRON_BASE,
    CHEVRON_OPEN
} from './OrganizeTab.styles';
import { OrganizedRoom } from "../types";
import { DraggableLineItem } from './DraggableLineItem';
import { CreateLineItemModal } from "./CreateLineItemModal";
import { LineItemWithMatchInfo } from '@/api/generated/client/models/LineItemWithMatchInfo';

interface CollapsibleRoomCardProps {
    room: OrganizedRoom;
    selectedItemIds: Set<number>;
    onToggleItemSelection: (id: number) => void;
    onSetItemsSelection: (itemIds: number[], isSelected: boolean) => void;
    isLocked: boolean;
    activeDragType?: 'insurer' | 'contractor' | null;
    activeDragId: number | null;
    onDeleteRoom: (roomId: number) => void;
    onDeleteLineItem: (itemId: number) => void;
    totalItemCount?: number;
    comparisonId: number;
    canWrite: boolean;
}

export function CollapsibleRoomCard({
    room,
    selectedItemIds,
    onToggleItemSelection,
    onSetItemsSelection,
    isLocked,
    activeDragType,
    activeDragId,
    onDeleteRoom,
    onDeleteLineItem,
    totalItemCount,
    comparisonId,
    canWrite
}: CollapsibleRoomCardProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState<any | null>(null);

    const { setNodeRef, isOver } = useDroppable({ id: room.id, disabled: isLocked });

    // Use passed total or fall back to current items length (for backward compat or if filtered wasn't modified)
    const effectiveTotalCount = totalItemCount ?? room.items.length;

    const selectedCount = room.items.filter(i => selectedItemIds.has(i.lineItemId!)).length;
    const unlockedItems = room.items.filter(i => !i.lineItemMatchGroupId);
    const hasUnlockedItems = unlockedItems.length > 0;
    const allUnlockedSelected = hasUnlockedItems && unlockedItems.every(i => selectedItemIds.has(i.lineItemId!));

    const isInsurer = room.type === 'insurer';
    const isValidTarget = !activeDragType || activeDragType === room.type;
    const shouldHighlight = isOver && !isLocked && isValidTarget;

    const handleCreate = (e: React.MouseEvent) => {
        e.stopPropagation();
        setItemToEdit(null);
        setIsCreateModalOpen(true);
    };

    const handleEdit = (item: LineItemWithMatchInfo) => {
        setItemToEdit({
            lineItemId: item.lineItemId!,
            description: item.description || '',
            quantity: item.qty,
            unit: item.uom,
            unitPriceCents: item.unitPriceCents,
            rcvCents: item.rcvCents,
            taxCents: item.taxCents,
            opCents: item.opCents,
            lineItemNumber: item.lineItemNumber
        });
        setIsCreateModalOpen(true);
    };

    return (
        <Card
            ref={setNodeRef}
            className={cn(
                ROOM_CARD_BASE,
                isInsurer ? ROOM_CARD_INSURER_BORDER : ROOM_CARD_CONTRACTOR_BORDER,
                shouldHighlight && ROOM_CARD_HIGHLIGHTED,
                isLocked && ROOM_CARD_LOCKED,
                activeDragType && !isValidTarget && ROOM_CARD_INVALID_TARGET
            )}
        >
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className="p-3 flex items-center gap-3">
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                        {/* Room not locked, but visual placeholder */}
                    </div>

                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-transparent">
                            <ChevronRight className={cn(CHEVRON_BASE, isOpen && CHEVRON_OPEN)} />
                        </Button>
                    </CollapsibleTrigger>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium break-words leading-tight">
                                {room.level ? `${room.level} - ${room.name}` : room.name}
                            </h4>
                            {selectedCount > 0 && <Badge variant="secondary" className="text-[10px] h-4 px-1">{selectedCount}</Badge>}

                            {/* Item Count Bubbles */}
                            {effectiveTotalCount === 0 ? (
                                <Badge variant="outline" className="text-[10px] h-4 px-1 text-muted-foreground font-normal border-slate-200">0 Items</Badge>
                            ) : (
                                <>
                                    <Badge variant="outline" className="text-[10px] h-4 px-1 text-muted-foreground font-normal border-slate-200 hover:bg-transparent whitespace-nowrap">
                                        {effectiveTotalCount} Items
                                    </Badge>
                                    {unlockedItems.length > 0 && (
                                        <Badge variant="secondary" className="text-[10px] h-4 px-1 font-normal bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-100 border whitespace-nowrap">
                                            {unlockedItems.length} Unmatched
                                        </Badge>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Show Select/Deselect All if there are UNLOCKED items */}
                    {unlockedItems.length > 0 && (
                        <div className="mr-3">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                    e.stopPropagation();

                                    if (allUnlockedSelected) {
                                        // Deselect All Logic
                                        const idsToDeselect = room.items
                                            .filter(i => selectedItemIds.has(i.lineItemId!))
                                            .map(i => i.lineItemId!);
                                        if (idsToDeselect.length > 0) {
                                            onSetItemsSelection(idsToDeselect, false);
                                        }
                                    } else {
                                        // Select All Unlocked Logic
                                        const idsToSelect = unlockedItems.map(i => i.lineItemId!);
                                        if (idsToSelect.length > 0) {
                                            onSetItemsSelection(idsToSelect, true);
                                        }
                                    }
                                }}
                            >
                                {allUnlockedSelected ? "Deselect All" : "Select All"}
                            </Button>
                        </div>
                    )}

                    <div className="flex items-center gap-1">
                        {/* Only show Add button if not locked (or maybe always allow add to unmatched groups? Room isn't match group though) */}
                        {!isLocked && (
                            <Button
                                size="icon"
                                className="h-6 w-6"
                                onClick={handleCreate}
                                title="Add Line Item"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        )}

                        {effectiveTotalCount === 0 && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteRoom(room.id);
                                }}
                                title="Delete Empty Room"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                        <div className="text-xs font-medium text-foreground mr-2">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(room.totalRCV)}
                        </div>
                    </div>
                </div>

                <CollapsibleContent>
                    <div className="px-3 pb-3 pt-0 space-y-1">
                        {room.items.map((item) => {
                            const isItemLocked = !!item.lineItemMatchGroupId;

                            // Check if this item is part of a bulk drag
                            const isBulkDragging = activeDragId !== null &&
                                selectedItemIds.has(activeDragId) &&
                                selectedItemIds.has(item.lineItemId!);

                            return (
                                <DraggableLineItem
                                    key={item.lineItemId}
                                    item={item}
                                    isSelected={selectedItemIds.has(item.lineItemId!)}
                                    onToggle={() => !isItemLocked && onToggleItemSelection(item.lineItemId!)}
                                    // Pass handleEdit only if item is not locked
                                    onEdit={isItemLocked ? undefined : handleEdit}
                                    onDelete={isItemLocked ? undefined : () => onDeleteLineItem(item.lineItemId!)}
                                    isLocked={isItemLocked}
                                    isGhosting={isBulkDragging}
                                />
                            );
                        })}
                    </div>
                </CollapsibleContent>
            </Collapsible>

            <CreateLineItemModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                roomId={room.id}
                comparisonId={comparisonId}
                canWrite={canWrite}
                initialData={itemToEdit}
            />
        </Card>
    );
}
